import React, {FC, useState} from 'react';
import {useApiService} from '../../context/api/useApiContext';
import {useTranslation} from 'react-i18next';
import {CandleType} from '../../enums/CandleType';
import {ICandle, ICandleLightRequest} from '../../types/resources/Candle';
import CandleIcon from './CandleIcon';
import styled from 'styled-components';
import ContentSelect from '../ContentSelect';
import TextInput from '../Forms/TextInput';
import { useCandleContext } from "../../context/candle/useCandleContext";
import {IOnlineCondolenceDetails} from '../../types/resources/OnlineCondolenceDetails';
import {useGoogleReCaptcha} from 'react-google-recaptcha-v3';
import {useSettingsContext} from '../../context/settings/useSettingsContext';

const CandleSelectGroup = styled.div`
    svg {
        height: 48px;
        width: 48px;
    }

    @media (max-width: 768px) {
        flex-wrap: wrap;
    }
`;

export type TProps = {
    onlineCondolence: IOnlineCondolenceDetails;
    hideForm: () => void;
    onCandleLit: (newCandle: ICandle) => void;
}

const CandleForm: FC<TProps> = ({ onlineCondolence, hideForm, onCandleLit }) => {
    const { t } = useTranslation();
    const { executeRecaptcha } = useGoogleReCaptcha();
    const { useGoogleRecaptchaV3 } = useSettingsContext();
    const { apiService, companyInfoId } = useApiService();
    const [selectedCandleType, setSelectedCandleType] = useState<string>(CandleType.Candle1);
    const [name, setName] = useState<string>('');

    const { candleCount, setCandleCount } = useCandleContext();

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        try {
            const candleData: ICandleLightRequest = {
                companyInfoId: onlineCondolence.companyInfoId ?? companyInfoId,
                onlineCondolenceId: onlineCondolence.id,
                authorName: name,
                mediaUrl: selectedCandleType,
            };

            if (useGoogleRecaptchaV3) {
                if (!executeRecaptcha) {
                    console.log('Execute recaptcha not yet available');
                    return;
                }

                candleData.recaptchaToken = await executeRecaptcha('handlePostSubmit');
            }

            const response = await apiService.lightOnlineCondolenceCandle(candleData);

            const newCandle: ICandle = {
                mediaUrl: selectedCandleType,
                authorName: name,
                createdOn: new Date(),
            };

            onCandleLit(newCandle);
            setCandleCount(candleCount + 1);
            hideForm();
        } catch (error) {
            console.error(error);
            // TODO: Inline error messages
        }

    };

    return (
        <div className="cr-candle-form cr-flex cr-flex-col cr-gap-lg cr-center cr-mb-xl">
            <div className="cr-form-title cr-text-lg cr-text-primary">
                {t('light-candle-title')}
            </div>
            <form
                onSubmit={handleSubmit}
                className="cr-flex cr-flex-col cr-gap"
            >
                <CandleSelectGroup className="cr-flex cr-gap cr-text-icon cr-candle-icon">
                    {Object.values(CandleType).map((type, index) =>
                        <ContentSelect
                            key={index}
                            radiogroup={'candle'}
                            value={type}
                            onSelect={() => setSelectedCandleType(type)}
                            isSelected={(selectedCandleType === type)}
                        >
                            <CandleIcon type={type}/>
                        </ContentSelect>
                    )}
                </CandleSelectGroup>
                <TextInput
                    label={t('name')}
                    name={'name'}
                    value={name}
                    setValue={setName}
                />
                <div className="cr-flex cr-gap cr-center cr-mt">
                    <button
                        className="cr-pill cr-bg-alert cr-text-white cr-text-base"
                        type={'submit'}
                    >
                        {t('light-your-candle')}
                    </button>
                    <button
                        className="cr-cancel cr-pill cr-border-primary cr-text-primary cr-text-base"
                        type={'button'}
                        onClick={hideForm}
                    >
                        {t('cancel')}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default CandleForm;
